import { BranchofficeData } from '@tymbe/schema/branchoffice.interface';
import {
  ActionIcon,
  AvIcon,
  CommunicationIcon,
  CustomIcon,
  DeviceIcon,
  EditorIcon,
  FavouriteIcon,
  ImageIcon,
  MapsIcon,
  PlacesIcon,
} from '@tymbe/ui-icons';
import feathersClient from '../../../../apiClient';
import {
  ArrayField,
  Form,
  FormApi,
  FormState,
  FormStateAccessor,
} from 'informed';
import { useCallback, useRef, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import classNames from 'classnames';
import ShiftPublishButton from './ShiftPublishButton';
import { timeFormatter, TyDateMultiPicker, TyInput, TyNumberInput, TyOption, TySelectInput } from '@tymbe/ui-forms';
import moment from 'moment/moment';
import Collapse from '../../../../components/Collapse';
import TyCheckbox from '../../../../components/inputs/TyCheckbox';
import { Query } from '@feathersjs/feathers';
import getResponseArray from '@tymbe/utils/getResponseArray';
import { Button } from '@tymbe/ui-primitives';
import TySelectRequirements from '../../../../components/inputs/TySelectRequirements';
import TySelectUtility from '../../../../components/inputs/TySelectUtility/TySelectUtility';
import { MomentInput } from 'moment';
import {
  ApplicationData,
  DocumentTypeData,
  isDocumentTypeData, isPerkData,
  ManShiftData,
  PerkData,
  PerkId,
  ShiftData,
} from '../../../../types/TymbeApi';
import { Gender, ShiftType } from '@tymbe/schema/enums';
import { getDurationString, maxDuration, queryFn } from './utils';
import TySelectInvitations from '../../../shift/create/components/TySelectInvitations/TySelectInvitations';
import { ShiftItem } from './types';
import ShiftListItem from './ShiftItem';
import CustomRange from './CustomRange';
import Tab from './Tab';
import { ErrorAlert, SuccessAlert } from '../../../../components/alerts';

type ShiftCreateProps = {
  departmentId: BranchofficeData['id'];
  onPublish?: () => void;
  onCancel?: () => void;
};

interface NewShiftForm extends Omit<ShiftItem, 'date'> {
  index?: number;
  startDates: MomentInput[];
}

interface ShiftsForm {
  shifts: ShiftItem[];
}

const ShiftCreate = ({ departmentId, onPublish, onCancel }: ShiftCreateProps) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [hasShifts, setHasShifts] = useState(false);
  const formApiRef = useRef<FormApi<ShiftsForm>>(undefined);
  const newShiftFormApiRef = useRef<FormApi<NewShiftForm>>(undefined);
  const invitationFormApiRef = useRef<FormApi<any>>(undefined);
  const shiftAddRef = useRef<(value: ShiftItem) => void>(undefined);

  const formApi = formApiRef.current;
  const newShiftFormApi = newShiftFormApiRef.current;
  const invitationFormApi = invitationFormApiRef.current;

  const [submitting, setSubmitting] = useState<boolean>(false);

  const { data: department } = useQuery({
    queryKey: ['branchoffice', departmentId] as const,
    queryFn,
  });

  const { data: company } = useQuery({
    queryKey: ['company', department?.company_id] as const,
    queryFn,
    enabled: !!department,
  });

  const { data: branchoffice } = useQuery({
    queryKey: ['branchoffice', department?.parent_id] as const,
    queryFn,
    enabled: !!department,
  });

  // TySelectShiftTemplate
  const { data: templates } = useQuery({
    queryKey: ['shift-template', 'select'] as const,
    queryFn: async () => {
      let query: Query = {
        company_id: company?.id,
        $eager: '[perk, documentType, branchoffice, utility]',
        $modify: { branchoffice: [department?.id] },
      };

      query = {
        ...query,
        $and: [{ publish_at: { $null: false } }, { publish_at: { $lt: moment().toISOString() } }],
      };

      return getResponseArray(await feathersClient.service('shift-template').find({ query }));
    },
    enabled: !!company?.id || !!department?.id,
  });

  const createShift = useMutation({
    mutationKey: ['shift'],
    mutationFn: async (data: Partial<ShiftData> | Partial<ShiftData>[]) => {
      return feathersClient.service('shift').create(data, { query: { $eager: 'manShift.perk' } });
    },
  });

  const checkInvitation = useMutation({
    mutationKey: ['invite-check'],
    mutationFn: async (data: any) => {
      return feathersClient.service('invite-check').create(data);
    },
  });

  const createInvitation = useMutation({
    mutationKey: ['application'],
    mutationFn: async (data: Partial<ApplicationData>[]) => {
      return feathersClient.service('application').create(data);
    },
  });

  const onAddShifts = ({ values, ...other }: FormState<NewShiftForm>) => {
    const { startDates = [], index, ...rest } = values;

    if (index !== undefined) {
      const initVal = formApi?.getValue<any>(`shifts[${index}]`);
      formApi?.setValue<any>(`shifts[${index}]`, {...initVal, ...rest, date: startDates[0]});
    } else {
      for (const date of startDates) {
        shiftAddRef.current?.({
          ...rest,
          date: date,
        });
      }
    }
    newShiftFormApi?.reset();
    setCurrentTab(1);
  }

  const onEditShift = (shift: ShiftItem, index: number) => {
    setCurrentTab(2);
    newShiftFormApi?.setValues({...shift, startDates: [moment(shift.date)], index});
  }

  const getInvitations = useCallback(() => {
    return invitationFormApi?.getValue('persons') || [];
  }, [invitationFormApi]);

  const publishShifts = async ({ values }: FormState<ShiftsForm>) => {
    const { shifts = [] } = values;
    const newShifts: Partial<ShiftData>[] = [];

    const company_id = company?.id;
    const branchoffice_id = department?.id;

    if (!company_id || !branchoffice_id) {
      return;
    }

    const now = moment();

    for (const shift of shifts) {
      if (!shift.templateId) continue;

      if (Number(shift.slots) < Number(shift.invitations?.length || 0)) {
        throw new Error('Počet pozvánek nesmí být větší než počet míst.');
      }

      const shiftTemplate = await feathersClient.service('shift-template').get(shift.templateId, { $eager: '[perk, documentType, utility]' });

      const [startHours, startMinutes] = shift.startTime.split(':').map(Number);
      const [endHours, endMinutes] = shift.endTime.split(':').map(Number);
      const addDay = (startMinutes + (startHours*60)) > (endMinutes + (endHours*60)) ? 1 : 0;

      const start_time = moment(shift.date)
        .set({ hour: startHours, minute: startMinutes });
      const end_time = moment(shift.date)
        .set({ hour: endHours, minute: endMinutes })
        .add(addDay, 'day');

      const manShifts: ManShiftData[] = [];

      if (shift.noPreferences) {
        for (let i = 0; i < Number(shift.slots); i++) {
          manShifts.push({} as ManShiftData);
        }
      } else if (shift.menOnly || shift.womenOnly) {
        for (let i = 0; i < Number(shift.slots); i++) {
          manShifts.push({ perk: [ { id: shift.menOnly ? PerkId.MAN_ONLY : PerkId.WOMAN_ONLY } as PerkData ] } as ManShiftData);
        }
      } else {
        const totalCount = Number(shift.slots);
        const menCount = Number(shift.men) || 0;
        const womenCount = Number(shift.women) || 0;

        for (let i = 0; i < totalCount; i++) {
          if (i < menCount) {
            manShifts.push({ perk: [ { id: PerkId.MAN_ONLY } as PerkData ] } as ManShiftData);
          } else if (i < (menCount + womenCount)) {
            manShifts.push({ perk: [ { id: PerkId.WOMAN_ONLY } as PerkData ] } as ManShiftData);
          } else {
            manShifts.push({} as ManShiftData);
          }
        }
      }

      const perks = new Set(shiftTemplate.perk);
      const docTypes = new Set(shiftTemplate.documentType);

      shift.requirements?.filter(isDocumentTypeData).forEach((docType) => {
        docTypes.add(docType);
      });
      shift.requirements?.filter(isPerkData).forEach((perk) => {
        perks.add(perk);
      })

      const contest_start = moment.min(now, start_time);
      const contest_end = moment.max(contest_start, start_time.clone().subtract(1, 'hour'));

      const shiftData: Partial<ShiftData> = {
        shift_template_id: shift.templateId,
        company_id,
        branchoffice_id,
        position_id: shiftTemplate.position_id,
        contact_person: shift.contact,
        name: shiftTemplate.name || shiftTemplate.template_name,
        payment_base: Number(shiftTemplate.payment_base),
        credits_min: Number(shiftTemplate.credits_min),
        credits: Number(shiftTemplate.credits),
        invitation_credits: Number(shiftTemplate.invitation_credits),
        billing_rate: Number(shiftTemplate.billing_rate),
        margin: Number(shiftTemplate.margin),
        contest_start: contest_start.toISOString(),
        contest_end: contest_end.toISOString(),
        start_time: start_time.toISOString(),
        end_time: end_time.toISOString(),
        description: shiftTemplate.description,
        instruction: shiftTemplate.instruction,
        instruction_newcomers: shiftTemplate.instruction_newcomers,
        pay_supplement: shiftTemplate.pay_supplement,
        job_classification_ids: shiftTemplate.job_classification_ids,
        expected_hpp_weekly_hours: shiftTemplate.expected_hpp_weekly_hours,
        maximum_hpp_trial_period: shiftTemplate.maximum_hpp_trial_period,
        type: ShiftType.REGULAR,
        emoji: shiftTemplate.emoji,

        manShift: manShifts,
        documentType: Array.from(docTypes).map(({ id }) => ({ id }) as DocumentTypeData),
        perk: Array.from(perks).map(({ id }) => ({ id }) as PerkData),
      };

      const invitations = (shift.invitations || []).map((invitation) => ({
        person_id: Number(invitation.id),
        shifts: [shiftData],
      }));

      await checkInvitation.mutateAsync(invitations);

      newShifts.push(shiftData);
    }

    const createdShifts = getResponseArray(await createShift.mutateAsync(newShifts));

    const applications = createdShifts.flatMap((shift, id) => {
      if (!shifts[id]?.invitations?.length) {
        return [];
      }

      const manShifts = shift.manShift || [];
      const forMan = manShifts.filter((manShift) => manShift.perk?.find(perk => perk.id === PerkId.MAN_ONLY));
      const forWoman = manShifts.filter((manShift) => manShift.perk?.find(perk => perk.id === PerkId.WOMAN_ONLY));
      const forOthers = manShifts.filter((manShift) => manShift.perk?.every(perk => ![PerkId.MAN_ONLY, PerkId.WOMAN_ONLY].includes(perk.id)));

      return shifts[id].invitations.map(async (invitation) => {
        const personData = await feathersClient.service('person-data').get(invitation.id);
        const manShift = (personData.gender === Gender.MALE ? forMan.pop() : forWoman.pop()) || forOthers.pop();

        if (!manShift) {
          throw new Error('Snažíte se pozvat více brigádníků než je volných míst.');
        }

        return {
          credits: Number(shift.invitation_credits),
          payment_base: Number(shift.payment_base),
          invitation: true,
          state: null,
          person_id: Number(invitation.id),
          man_shift_id: manShift.id,
          employer_id: shift.company_id,
        } satisfies Partial<ApplicationData>;
      });
    });

    await createInvitation.mutateAsync(await Promise.all(applications));
  }

  const onSubmit = async (state: FormState<ShiftsForm>) => {
    setSubmitting(true);
    try {
      await publishShifts(state);
      onPublish?.();
      SuccessAlert('Směny byly úspěšně publikovány.');
    } catch (e) {
      ErrorAlert(String(e));
    }
    setSubmitting(false);
  }

  return (
    <div className="w-full h-full flex flex-col">
      <div className="p-6 flex align-center border-b border-secondary-100 text-heading-4-xl">
        Vypsat směny
      </div>
      <div className="flex p-6 pb-0 *:pb-6 gap-10 items-start overflow-auto">
        <div className="flex flex-col gap-3 w-[512px] h-full overflow-auto">
          <ul className="flex items-center text-caption-xl gap-1.5">
            <li>{company?.name}</li>
            <li><FavouriteIcon name="chevron_right" className="size-4 p-0.5 bg-secondary-transparent-5" /></li>
            <li>{branchoffice?.name}</li>
            <li><FavouriteIcon name="chevron_right" className="size-4 p-0.5 bg-secondary-transparent-5" /></li>
            <li>{department?.name}</li>
          </ul>
          <div className="bg-secondary-50 rounded-3xl flex flex-col overflow-auto">
            <div className="flex px-4 border-b border-secondary-200 min-h-16 items-stretch">
              <Tab
                onClick={() => setCurrentTab(0)}
                active={currentTab === 0}
                disabled={currentTab === 2}
              >
                Nové směny
              </Tab>
              <Tab
                title={'Nejprve vytvořte směny'}
                onClick={() => setCurrentTab(1)}
                disabled={!hasShifts || currentTab === 2}
                active={currentTab === 1}
              >
                Pozvat Tymbery
              </Tab>
              <Tab
                disabled={currentTab!==2}
                active={currentTab === 2}
              >
                Upravit směnu
              </Tab>
            </div>
            <Form<NewShiftForm> className="flex flex-col p-8 gap-6 overflow-auto" formApiRef={newShiftFormApiRef} onSubmit={onAddShifts} hidden={![0, 2].includes(currentTab)} >
              <TyInput name="index" className="hidden" />
              <div className="flex flex-col gap-4">
                <div className="flex gap-2 items-center grow">
                  <PlacesIcon name="business_center" className="size-5" />
                  <TySelectInput name="templateId" className="grow" required>
                    {templates?.map((template) => (
                      <TyOption key={template.id} value={template.id} label={template.template_name}>
                        {template.template_name}
                      </TyOption>
                    ))}
                  </TySelectInput>
                </div>

                <div className="flex gap-2 items-center grow">
                  <EditorIcon name="insert_invitation" className="size-5 shrink-0" />
                  <TyDateMultiPicker name="startDates" required />
                </div>

                <div className="flex gap-2 items-center grow">
                  <DeviceIcon name="access_time_filled" className="size-5 shrink-0" />
                  <div className="flex gap-2 items-center">
                    <TyInput name="startTime" formatter={timeFormatter} validateWhen={['endTime']} validate={maxDuration} required />
                    -
                    <TyInput name="endTime" formatter={timeFormatter} validateWhen={['startTime']} validate={maxDuration} required />
                    <FormStateAccessor<NewShiftForm>>
                      {(formState) => <span className="text-nowrap">{getDurationString(formState.values.startTime, formState.values.endTime)}</span>}
                    </FormStateAccessor>
                  </div>
                </div>

                <div className="flex gap-2 items-center grow">
                  <CommunicationIcon name="phone" className="size-5 shrink-0" />
                  <TyInput name="contact" required />
                </div>
              </div>

              <div className="flex flex-col gap-4">
                Místa

                <div className="flex gap-2 items-center grow">
                  <div className="flex gap-2 items-center">
                    <ActionIcon name="supervisor_account" className="size-5 shrink-0" />
                    <TyNumberInput name="slots" min={1} required />
                    <div className="flex gap-2 items-center leading-none w-full justify-end">
                      <TyCheckbox name="noPreferences" />
                      <span className="text-body-2-xl text-secondary-500">Nemám preferenci</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 items-center grow">
                  <FormStateAccessor<NewShiftForm>>
                    {({ values }) => {
                      return (
                        <>
                          <CustomRange
                            name="men"
                            label="Minimální počet mužů"
                            icon={<FavouriteIcon name="male" className="size-5" />}
                            max={(Number(values.slots) || 0) - (Number(values.women) || 0)}
                            disabled={values.noPreferences || values.womenOnly}
                            suffix={<TyCheckbox name="menOnly" label="Pouze muži" />}
                          />
                          <CustomRange
                            name="women"
                            label="Minimální počet žen"
                            icon={<FavouriteIcon name="female" className="size-5" />}
                            max={(Number(values.slots) || 0) - (Number(values.men) || 0)}
                            disabled={values.noPreferences || values.menOnly}
                            suffix={<TyCheckbox name="womenOnly" label="Pouze ženy" />}
                          />
                        </>
                      );
                    }}
                  </FormStateAccessor>
                </div>
              </div>

              <Collapse
                toggler={<Button className="border gap-2" type="button">Další nastavení <FavouriteIcon name="chevron_down" className="size-5 [.collapse-open_&]:rotate-180" /></Button>}
              >
                <div className="flex flex-col gap-6 mt-6">
                  <div className="flex gap-2 items-center grow">
                    <CustomIcon name="label2" className="size-5 shrink-0" />
                    <TySelectRequirements
                      name="requirements"
                      className="grow [&_.ty\_select\_\_menu-portal]:!left-auto [&_.ty\_select\_\_menu-portal]:!top-auto [&_.ty\_select\_\_menu-portal]:mt-3"
                      menuPosition="fixed"
                      isMulti
                    />
                  </div>
                  <div className="flex gap-2 items-center grow">
                    <MapsIcon name="dry_cleaning" className="size-5 shrink-0" />
                    <TySelectUtility
                      name="utilities"
                      className="grow [&_.ty\_select\_\_menu-portal]:!left-auto [&_.ty\_select\_\_menu-portal]:!top-auto [&_.ty\_select\_\_menu-portal]:mt-3"
                      menuPosition="fixed"
                      isMulti
                    />
                  </div>
                </div>
              </Collapse>


              <FormStateAccessor<NewShiftForm>>
                {({ values }) => (
                  <>
                  <div className="flex gap-3" hidden={values.index !== undefined}>
                    <Button className="border gap-2" type="reset">
                      <AvIcon name="replay" className="size-4" />
                      Resetovat
                    </Button>
                    <Button className="gap-2 bg-secondary-600 text-white">
                      <ImageIcon name="add_to_photos" className="size-5" /> Přidat směny
                    </Button>
                  </div>
                    <div className="flex gap-3" hidden={values.index === undefined}>
                      <Button className="border gap-2" type="reset" onClick={() => setCurrentTab(1)}>
                        <FavouriteIcon name="close" className="size-4" /> Zahodit změny
                      </Button>
                      <Button className="gap-2 bg-secondary-600 text-white">
                        <FavouriteIcon name="check" className="size-4" /> Uložit úpravy
                      </Button>
                    </div>
                  </>
                )}
              </FormStateAccessor>
            </Form>

            <Form<ShiftsForm> className="flex flex-col p-8 gap-6 overflow-auto" formApiRef={invitationFormApiRef} hidden={currentTab !== 1}>
              <div className="flex gap-2 items-start grow">
                <ActionIcon name="info" className="size-4 shrink-0" />
                <div className="text-body-2-xl text-secondary-500">
                  Níže si vytvořte seznam všech Tymberů, se kterými chcete pracovat. Na směny pak budete moct přidat pracovníky jednotlivě.
                </div>
              </div>
              <div className="flex gap-2 items-center grow">
                <ActionIcon name="supervisor_account" className="size-4 shrink-0" />
                <TySelectInvitations
                  name="persons"
                  className="w-full *:!p-2 *:!h-[42px] *:!min-h-min"
                  companyId={company?.id}
                  branchOfficeId={branchoffice?.id}
                  isMulti
                />
              </div>
              <div className="flex gap-2 items-center grow justify-end">
                <Button className="border border-red-300 text-red-500 gap-2 px-3 py-2" type="reset"><ActionIcon name="delete_outline" className="size-4" />Vyčistit</Button>
              </div>
            </Form>
          </div>
        </div>
        <div className="h-full grow overflow-auto [scrollbar-gutter:stable] scrollbar-visible">
          <Form className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-x-10 items-center font-medium text-body-2-xl pr-4" formApiRef={formApiRef} onSubmit={onSubmit} id="newShiftsForm">
            <FormStateAccessor<ShiftsForm>>
              {({ values }) => { setHasShifts(() => !!values.shifts?.length); }}
            </FormStateAccessor>
            <div className="grid grid-cols-subgrid col-span-5 px-4 auto-rows-[theme('spacing.16')] items-center">
              <div>Datum</div>
              <div>Čas</div>
              <div>Míst</div>
              <div>Pozvánky</div>
            </div>

            <div className="contents">
              <ArrayField name="shifts">
                {({ addWithInitialValue, ...rest }) => {
                  shiftAddRef.current = addWithInitialValue;

                  return (
                    <ArrayField.Items>
                      {() => {
                        return (
                          <ShiftListItem onEdit={onEditShift} getInvitations={getInvitations} />
                      )}}
                    </ArrayField.Items>
                  )
                }}
              </ArrayField>
            </div>
          </Form>
        </div>
      </div>

      <div className="flex-1" />
      <div className="flex justify-between items-center gap-3 py-4 px-6 border-t border-secondary-100">
        <div className={
          classNames(
            'p-2 border border-error-500 rounded-lg bg-error-100 text-error font-medium flex gap-4',
            { invisible: true },
          )
        }
        >
          {{}?.msg}
          <button
            type="button"
            className="p-0 rounded-none underline hover:text-error-600"
          >
            Detail
          </button>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="py-2 px-4 border text-sm font-semibold border-secondary-300"
            onClick={() => {
              onCancel?.();
            }}
          >Zrušit
          </button>
          <ShiftPublishButton
            form="newShiftsForm"
            disabled={!hasShifts || submitting}
            isPublishing={submitting}
          />
        </div>
      </div>
    </div>
  );
};

export default ShiftCreate;
